import React, { useState, useEffect, useRef } from "react";
import styles from "./RemindersDrawer.module.scss";
import { useDispatch } from "react-redux";
import { Avatar } from "@fluentui/react-components";
import { Ellipsis, Trash2, Edit, AlarmClock, Dot, Users, Check, X } from 'lucide-react';
import { deleteReminderRequest, fetchReminderRequest } from "../../store/actions/general.actions";
import { utcToLocalDateString, utcToLocalDateTimeString } from "../../utils/systemSettings";
import HoverCard from "../hoverCard/HoverCard";
import moment from "moment/moment";
import { api as generalService } from '../../store/services/general.service';
import { REDIRECTION_TYPES } from '../../helpers/utils';
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const SelfCreated = ({ item, setIsModalOpen, modalType, setIsCreate, setReminder }) => {
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const dispatch = useDispatch();
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  const [displayTitle, setDisplayTitle] = useState("");
  const [displayNotes, setDisplayNotes] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [hasLongContent, setHasLongContent] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showReadStatus, setShowReadStatus] = useState(false);
  const notesRef = useRef(null);
  const menuRef = useRef(null);
  const readStatusRef = useRef(null);
  const cardRef = useRef(null);
  const history = useHistory();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (readStatusRef.current && !readStatusRef.current.contains(event.target)) {
        setShowReadStatus(false);
      }
    };

    if (showMenu || showReadStatus) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, showReadStatus]);

  const editReminder = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setIsModalOpen(true);
    modalType("reminder");
    setIsCreate(false);
    dispatch(fetchReminderRequest(item.id));
  };

  const deleteReminder = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    dispatch(deleteReminderRequest(item.id));
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const transformLinks = (text, links) => {
    if (!text || text.trim() === "" || !text.includes("<@link:"))
      return text;
    
    const regex = /<@link:(\w+):([a-f0-9\-]+)@>/g;
    const transformedString = text.replace(regex, (match, type, id) => {
      let entityName;
      if (links && links?.length > 0) {
        entityName = links.filter((e) => e.linkId === id).map((e) => e.linkName);
      }
      console.log("Transforming link typein text:", type);
      let linkUrl = "";
      if (type === "Client" || type === "Project" || type === "Scope")
        linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}s/${id}/overview`;
      else if (type === "Portfolio" || type === "Program")
        linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}/${id}/overview`;
      else if (type === "ChangeRequest" || type === "CommunicationPlan" || type === "Budget" || type === "Risk" || type === "Task")
        return `<a target="_blank" class="notification-redirection-link cursor-pointer" data-type="${type.toLowerCase()}" data-id="${id}">${entityName}</a>`;

      if (linkUrl !== "")
        return `<a href=${linkUrl} target="_self" data-type="${type}" data-id="${id}">${entityName}</a>`;
      else
        return `<span class="text-primary">${entityName}</span>`;
    });
    return transformedString;
  };

  useEffect(() => {
    const handleLinkClick = (e) => {
      const target = e.target;
      // Check if the clicked element has the redirection class
      if (target.classList.contains('notification-redirection-link')) {
        e.preventDefault();
        e.stopPropagation();

        const type = target.getAttribute('data-type');
        const id = target.getAttribute('data-id');

        if (type && id) {
          generalService.getNotificationLinkRedirectionRequest({ type, id })
            .then((response) => {
              if (response) {
                if (response.scopeId)
                  handleScopeRedirection(response.scopeId, id);

                else if (response.projectId)
                  handleProjectRedirection(response.projectId, type, id);

              }
            }).catch((error) => {
            });
        }
      }
    };

    const cardElement = cardRef.current;
    if (cardElement) {
      cardElement.addEventListener('click', handleLinkClick);
    }

    return () => {
      if (cardElement) {
        cardElement.removeEventListener('click', handleLinkClick);
      }
    };
  }, []);
  const handleScopeRedirection = (scopeId, taskId) => {
    history.push(`/scopes/${scopeId}/overview?r_id=${taskId}`);
  };

  const handleProjectRedirection = (projectId, type, id) => {
    if (projectId && type && id) {
      let redirectionType;
      switch (type) {
        case 'changerequest':
          redirectionType = REDIRECTION_TYPES.CHANGE_REQUEST;
          break;
        case 'communicationplan':
          redirectionType = REDIRECTION_TYPES.COMMUNICATION;
          break;
        case 'budget':
          redirectionType = REDIRECTION_TYPES.BUDGET;
          break;
        case 'risk':
          redirectionType = REDIRECTION_TYPES.RISK;
          break;
        case 'task':
          redirectionType = REDIRECTION_TYPES.TASK;
          break;
        default:
          return;
      }

      history.push(`/projects/${projectId}/overview?r_t=${redirectionType}&r_id=${id}`);
    }

  };

  useEffect(() => {
    setDisplayTitle(transformLinks(item?.title, item?.links));
    setDisplayNotes(transformLinks(item?.notes, item?.links));
  }, [item?.title, item?.notes]);

  useEffect(() => {
    if (notesRef.current && displayNotes) {
      const lineHeight = parseInt(getComputedStyle(notesRef.current).lineHeight);
      setHasLongContent(notesRef.current.scrollHeight > lineHeight * 2);
    }
  }, [displayNotes]);

  const getPriorityClass = () => {
    switch (item?.priority || 0) {
      case 2: return styles.priorityHigh;
      case 1: return styles.priorityModerate;
      default: return styles.priorityLow;
    }
  };

  const getTimeDifference = (dateString) => {
    if (!dateString) return '';

    const now = moment();
    const targetDate = moment.utc(dateString).local();
    const duration = moment.duration(targetDate.diff(now));
    const isPast = duration.asSeconds() < 0;

    const absDuration = moment.duration(Math.abs(duration.asMilliseconds()));
    const days = Math.floor(absDuration.asDays());
    const hours = Math.floor(absDuration.asHours() % 24);
    const minutes = Math.floor(absDuration.asMinutes() % 60);
    const seconds = Math.floor(absDuration.asSeconds() % 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    if (parts.length === 0) {
      if (seconds < 10) return 'now';
      return isPast ? 'just now' : 'in a few seconds';
    }

    const timeString = parts.join(' ');

    return isPast ? `${timeString} ago` : `in ${timeString}`;
  };

  const getReadCount = () => {
    if (!item?.readLog || item.readLog.length === 0) return 0;
    return item.readLog.filter(log => log.isRead).length;
  };

  const getTotalRecipients = () => {
    return item?.readLog?.length || 0;
  };

  const handleReadStatusToggle = (e) => {
    e.stopPropagation();
    setShowReadStatus(!showReadStatus);
  };

  return (
    <div 
      ref={cardRef} 
      className={`${styles.remindCardWrapper} ${getPriorityClass()} ${expanded ? styles.expanded : ''} ${item?.dateSent ? styles.isSent : ''}`}>
      {
        !item?.dateSent && (
          <div className={styles.actionMenu} ref={menuRef}>
            <button className={styles.menuTrigger} onClick={toggleMenu}>
              <Ellipsis size={16} />
            </button>
            {showMenu && (
              <div className={styles.menuDropdown}>
                <button className={styles.menuItem} onClick={editReminder}>
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={deleteReminder}>
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        )
      }

      <div className={styles.avatarWrapper}>
        {item?.creator ? (
          <HoverCard
            HoverCardTrigger={
              <div>
                <Avatar
                  size={28}
                  color="colorful"
                  name={item.creator.name}
                  image={{ src: item.creator.image }}
                />
              </div>
            }
            AvatarId={item?.creator?.id}
            AvatarName={item?.creator?.name}
            AvatarImage={item?.creator?.image}
            AvatarDesignation={item?.creator?.title}
            AvatarEmail={item?.creator?.email}
            AvatarPhone={item?.creator?.phoneNumber}
            AvatarIsExternal={item?.creator?.isExternal}
            CanSendMessage={true}
          />
        ) : (
          <Avatar size={28} color="blue" />
        )}
      </div>

      <div className={styles.remindCardContent}>
        <div className={styles.titleWrapper}>
          {displayTitle && (
            <p className={styles.reminderTitle}>
              <span dangerouslySetInnerHTML={{ __html: displayTitle }} />
            </p>
          )}
          {displayNotes && (
            <>
              <p ref={notesRef} className={styles.reminderNotes}>
                <span dangerouslySetInnerHTML={{ __html: displayNotes }} />
              </p>
              {hasLongContent && (
                <button className={styles.readMoreButton} onClick={handleExpand}>
                  {expanded ? (
                    <>
                      Show less
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </>
                  ) : (
                    <>
                      Read more
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {item?.recipients && item.recipients.length > 0 && (
          <div className={styles.recipientsSection}>
            <div className={styles.recipientsList}>
              {item.recipients.map((recipient, index) => (
                <div key={index} className={styles.recipientTag}>
                  <HoverCard
                    HoverCardTrigger={
                      <div className={styles.recipientAvatar}>
                        <Avatar
                          size={20}
                          color="colorful"
                          name={recipient.name}
                          image={{ src: recipient.image }}
                        />
                      </div>
                    }
                    AvatarId={recipient?.id}
                    AvatarName={recipient?.name}
                    AvatarImage={recipient?.image}
                    AvatarDesignation={recipient?.title}
                    AvatarEmail={recipient?.email}
                    AvatarPhone={recipient?.phoneNumber}
                    AvatarIsExternal={recipient?.isExternal}
                    CanSendMessage={true}
                  />
                  <span className={styles.recipientName}>{recipient.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.reminderMeta}>
          {item?.dueDate && (
            <span className={styles.dueDate}>
              <AlarmClock size={14} />
              {utcToLocalDateTimeString(setting, item?.dueDate)}
              <Dot size={10} />
              <span>{getTimeDifference(item?.dueDate)}</span>
            </span>
          )}

          {getTotalRecipients() > 0 && (
            <div className={styles.readStatusContainer} ref={readStatusRef}>
              <button
                className={styles.readStatusBadge}
                onClick={handleReadStatusToggle}
                title="View read status"
              >
                <Users size={14} />
                <span>{getReadCount()}/{getTotalRecipients()}</span>
              </button>

              {showReadStatus && (
                <div className={styles.readStatusPopup}>
                  <div className={styles.readStatusHeader}>
                    <span className={styles.readStatusTitle}>Read Status</span>
                    <span className={styles.readStatusCount}>
                      {getReadCount()} of {getTotalRecipients()} read
                    </span>
                  </div>
                  <div className={styles.readStatusList}>
                    {item.readLog.map((log, index) => (
                      <div key={index} className={styles.readStatusItem}>
                        <div className={styles.readStatusUser}>
                          <Avatar
                            size={20}
                            color="colorful"
                            name={log.user.name}
                            image={{ src: log.user.image }}
                          />
                          <span className={styles.readStatusUserName}>
                            {log.user.name}
                          </span>
                        </div>
                        <div className={styles.readStatusIndicator}>
                          {log.isRead ? (
                            <div className={styles.readStatusRead}>
                              <Check size={16} />
                            </div>
                          ) : (
                            <div className={styles.readStatusUnread}>
                              <X size={16} />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfCreated;
